import { CircleDot, Gauge, Wrench, RefreshCw, Move, Activity, Sun, Snowflake, CloudSun } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pathFor } from "@/i18n/translations";
import { IMAGES } from "@/config/images";
import { Reveal } from "@/components/Reveal";
import { PageHero, AccentButton, OutlineButton, Overline } from "@/components/ui-kit";
import { BrandMarquee } from "@/components/BrandMarquee";
import { ServiceWhatsApp } from "@/components/ContactWidgets";

const CARD_ICONS = [CircleDot, Gauge, Wrench, RefreshCw, Move, Activity];
const SEASON_ICONS = [Sun, Snowflake, CloudSun];

export default function Tires() {
  const { lang, t } = useLang();
  const p = t.tiresPage;
  return (
    <div>
      <PageHero image={IMAGES.tireBalance} alt={t.alts.tiresHero} overline={t.tiresPreview.overline} titleLines={[p.heroTitle]} subtitle={p.heroSub} />

      {/* Brands strip */}
      <section data-testid="tire-brands-section" className="pt-20 md:pt-28 bg-[var(--bg-primary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 mb-10 md:mb-14">
          <Reveal>
            <Overline>{p.brandsOverline}</Overline>
            <h2 className="mt-4 font-display font-semibold text-white text-3xl sm:text-4xl max-w-2xl">{p.brandsTitle}</h2>
          </Reveal>
        </div>
        <BrandMarquee />
      </section>

      {/* Service cards */}
      <section className="py-24 md:py-32 bg-[var(--bg-primary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {p.cards.map((c, i) => {
              const Ico = CARD_ICONS[i % CARD_ICONS.length];
              return (
                <Reveal key={i} delay={(i % 3) * 0.05}>
                  <div data-testid={`tire-card-${i}`} className="group bg-[var(--bg-secondary)] p-8 md:p-10 h-full hover:bg-[var(--bg-tertiary)] transition-colors">
                    <Ico size={28} className="text-white group-hover:text-[var(--accent)] transition-colors" strokeWidth={1.4} />
                    <h3 className="mt-6 font-display text-xl font-medium text-white">{c.name}</h3>
                    <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
                    <div className="mt-5"><ServiceWhatsApp service={c.name} testid={`tire-card-wa-${i}`} /></div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Choose the right tire */}
      <section className="py-24 md:py-32 bg-[var(--bg-secondary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <Reveal>
            <Overline>{t.nav.tires}</Overline>
            <h2 className="mt-4 font-display font-semibold text-white text-4xl sm:text-5xl">{p.chooseTitle}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {p.choose.map((c, i) => {
              const Ico = SEASON_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div data-testid={`tire-season-${i}`} className="bg-[var(--bg-primary)] p-10 h-full">
                    <Ico size={34} style={{ color: "var(--accent)" }} strokeWidth={1.4} />
                    <h3 className="mt-7 font-display text-2xl font-medium text-white">{c.name}</h3>
                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Note + CTA */}
      <section className="py-24 md:py-28 bg-[var(--bg-primary)]">
        <div className="mx-auto max-w-3xl px-5 md:px-8 text-center">
          <Reveal>
            <p className="font-display text-2xl sm:text-3xl text-white font-medium leading-snug">{p.note}</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <AccentButton to={pathFor(lang, "contact")} testid="tires-offer-btn">{t.cta.tireQuote}</AccentButton>
              <OutlineButton to={pathFor(lang, "contact")} testid="tires-book-btn">{t.cta.book}</OutlineButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
